import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { MAIN_BRANCH_ID } from '@polyai/stream/constants';
import type { ConflictResolution } from '@polyai/stream/services/conflict-resolution';

import { HttpStatusCode } from 'api/types';
import useDeleteDraft from 'hooks/agentStream/draft/useDeleteDraft';
import useMergeDraft from 'hooks/agentStream/draft/useMergeDraft';
import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import { handleTestSetRunsTriggered } from 'hooks/useDeployments/utils';
import useProjectRoute from 'hooks/useProjectRoute';
import { useProjectSettings } from 'hooks/useProjectSettings';
import { useToast } from 'hooks/useToast';
import { setBranchId } from 'store/reducers/stream-tracker';

export const useDraftManager = () => {
  const dispatch = useLocalDispatch();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { accountId, projectId } = useProjectRoute();

  // Disable validation when multi-draft is disabled (e.g., in PLG prod or template engine projects)
  const { isMultiDraftEnabled, isTemplateEngineEnabled } = useProjectSettings();
  const disableValidation = !isMultiDraftEnabled;

  const lastKnownSequence = useAppSelector(
    state => state.streamTracker.lastKnownSequence,
  );
  const { branchId } = useAppSelector(state => state.streamTracker);

  const { mutateAsync: deleteDraftMutation, isLoading: isDeletingDraft } =
    useDeleteDraft();

  const { mutateAsync: mergeDraftMutation, isLoading: isMergingDraft } =
    useMergeDraft();

  const deleteDraft = useCallback(() => {
    if (!lastKnownSequence || !branchId) {
      return Promise.reject(new Error('No branch ID or last known sequence'));
    }

    return deleteDraftMutation(
      {
        branchId: branchId,
        expectedBranchLastKnownSequence: lastKnownSequence,
      },
      {
        onSuccess() {
          dispatch(setBranchId({ branchId: MAIN_BRANCH_ID }));
          toast.success({
            title: isTemplateEngineEnabled ? 'Draft deleted' : 'Branch deleted',
          });
        },
      },
    );
  }, [
    lastKnownSequence,
    branchId,
    deleteDraftMutation,
    dispatch,
    toast,
    isTemplateEngineEnabled,
  ]);

  const mergeDraft = useCallback(
    (
      deploymentMessage?: string,
      conflictResolutions?: ConflictResolution[],
    ) => {
      if (!lastKnownSequence || !branchId) {
        return Promise.reject(new Error('No branch ID or last known sequence'));
      }

      return mergeDraftMutation(
        {
          branchId: branchId,
          expectedBranchLastKnownSequence: lastKnownSequence,
          deploymentMessage,
          conflictResolutions,
          disableValidation,
        },
        {
          onSuccess(res) {
            if (
              res.statusCode !== HttpStatusCode.ClientErrorBadRequest &&
              res.statusCode < HttpStatusCode.ClientErrorBadRequest
            ) {
              handleTestSetRunsTriggered(
                res.data.testRunIds,
                toast,
                accountId,
                projectId,
                queryClient,
              );
              toast.success({
                title: isTemplateEngineEnabled
                  ? 'Draft saved as version'
                  : 'Branch merged',
              });
            }
          },
        },
      );
    },
    [
      lastKnownSequence,
      branchId,
      mergeDraftMutation,
      toast,
      accountId,
      projectId,
      queryClient,
      disableValidation,
      isTemplateEngineEnabled,
    ],
  );

  return {
    deleteDraft,
    isDeletingDraft,
    mergeDraft,
    isMergingDraft,
  };
};
